import { Button } from '@helix-ds/react'
import React, { useState } from 'react'
import { useIntl } from 'react-intl'
import { useLocation, useNavigate } from 'react-router-dom'
import { useFamilyPortalApiClient } from '../features/auth/hooks/useFamilyPortalApiClient'
import { todayIsoDate } from '../utils/date'

const CakeIcon: React.FC = () => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" className="h-14 w-14" aria-hidden="true">
    <path
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={1.5}
      d="M8 8V5m0-2 1 2-1-2Zm8 5V5m0-2 1 2-1-2Zm-4 5V5m0-2 1 2-1-2ZM3 11h18M3 11a1 1 0 0 0-1 1v8a1 1 0 0 0 1 1h18a1 1 0 0 0 1-1v-8a1 1 0 0 0-1-1M3 11m0 5c1.5-.667 3-.667 4.5 0s3 .667 4.5 0 3-.667 4.5 0"
    />
  </svg>
)

function isValidDate(value: string): boolean {
  if (!value) return false
  const d = new Date(value)
  return !Number.isNaN(d.getTime())
}

function isFutureDate(value: string): boolean {
  // Compare ISO calendar dates as strings (lexicographic order matches
  // chronological order for YYYY-MM-DD). Avoids the timezone shift that
  // `new Date('YYYY-MM-DD')` (UTC midnight) introduces when compared against a
  // local "today".
  return value > todayIsoDate()
}

const DOBCapture: React.FC = () => {
  const intl = useIntl()
  const apiClient = useFamilyPortalApiClient()
  const navigate = useNavigate()
  const location = useLocation()

  const [dob, setDob] = useState('')
  const [validationError, setValidationError] = useState<string | null>(null)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [saveError, setSaveError] = useState(false)

  const handleDobChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setDob(e.target.value)
    setValidationError(null)
    setSaveError(false)
  }

  const validate = (): boolean => {
    if (!isValidDate(dob)) {
      setValidationError(
        intl.formatMessage({ id: 'dobCapture.validation.invalid', defaultMessage: 'Please enter a valid date.' })
      )
      return false
    }
    if (isFutureDate(dob)) {
      setValidationError(
        intl.formatMessage({
          id: 'dobCapture.validation.future',
          defaultMessage: 'Date of birth cannot be in the future.',
        })
      )
      return false
    }
    return true
  }

  const handleContinue = async () => {
    if (!validate()) return
    setIsSubmitting(true)
    setSaveError(false)
    try {
      const res = await apiClient.patch('/profile/demographics', { dateOfBirth: dob })
      if (!res.ok) throw new Error('save failed')
      navigate('/home', { state: location.state, replace: true })
    } catch {
      setIsSubmitting(false)
      setSaveError(true)
    }
  }

  const isDateReady = dob && isValidDate(dob) && !isFutureDate(dob)

  return (
    <div className="min-h-screen" style={{ backgroundColor: 'var(--color-bg-secondary)' }}>
      <div className="flex justify-center px-4 pt-12 pb-8">
        <img src="/icons/Inflection_Logo_Dark_LG.png" alt="Inflection Medicine" className="h-10 w-auto sm:h-[42px]" />
      </div>

      <div className="mx-auto w-full max-w-[1024px] px-4 sm:px-8">
        <div
          className="flex flex-col gap-8 rounded-3xl border p-8"
          style={{
            backgroundColor: 'var(--color-bg-primary)',
            borderColor: 'var(--color-border-base)',
            boxShadow: '0px 25px 50px -12px rgba(29, 41, 61, 0.25)',
          }}
        >
          <div
            className="flex h-24 w-24 items-center justify-center rounded-full"
            style={{ backgroundColor: 'var(--color-success-50)', color: 'var(--color-success-700)' }}
          >
            <CakeIcon />
          </div>

          <h1
            className="text-2xl leading-tight font-semibold tracking-tight sm:text-4xl sm:leading-9"
            style={{ color: 'var(--color-text-heading)' }}
          >
            {intl.formatMessage({ id: 'dobCapture.heading', defaultMessage: 'Enter your date of birth' })}
          </h1>

          <div
            className="rounded-lg border p-4"
            style={{
              backgroundColor: 'var(--color-bg-secondary-soft)',
              borderColor: 'var(--color-border-base-strong)',
            }}
          >
            <p className="text-sm leading-5" style={{ color: 'var(--color-fg-success-strong, #004f3b)' }}>
              {intl.formatMessage({
                id: 'dobCapture.well',
                defaultMessage: 'Your date of birth is required before any consent or sample collection steps.',
              })}
            </p>
          </div>

          <div className="flex flex-col gap-1.5">
            <label htmlFor="dob-input" className="sr-only">
              {intl.formatMessage({ id: 'dobCapture.label', defaultMessage: 'Date of birth' })}
            </label>
            <input
              id="dob-input"
              type="date"
              value={dob}
              onChange={handleDobChange}
              disabled={isSubmitting}
              max={todayIsoDate()}
              className={`border-border-base focus:ring-brand-600/20 focus:border-brand-600 w-full rounded-xl border bg-white px-4 py-2.5 text-base focus:ring-2 focus:outline-none disabled:cursor-not-allowed dark:border-gray-700 dark:bg-gray-900 dark:text-white${validationError ? 'border-red-500' : ''}`}
              aria-describedby={validationError ? 'dob-error' : undefined}
              aria-invalid={!!validationError}
            />
            {validationError && (
              <p id="dob-error" role="alert" className="text-sm" style={{ color: 'var(--color-error-600, #dc2626)' }}>
                {validationError}
              </p>
            )}
          </div>

          {saveError && (
            <p role="alert" className="text-sm" style={{ color: 'var(--color-error-600, #dc2626)' }}>
              {intl.formatMessage({
                id: 'dobCapture.error.save',
                defaultMessage: 'Failed to save your date of birth. Please try again.',
              })}
            </p>
          )}

          <div className="flex justify-end">
            <Button variant="primary" size="lg" disabled={!isDateReady || isSubmitting} onClick={handleContinue}>
              {intl.formatMessage({ id: 'dobCapture.footer.continue', defaultMessage: 'Continue' })}
            </Button>
          </div>
        </div>
      </div>
    </div>
  )
}

export default DOBCapture
